/**
 * MetricTooltip (W1c) — lightweight per-stat explanation affordance.
 *
 * A hover [i] that shows a short explanation of what a statistic measures. Pass
 * `description` inline, or a `fieldKey` that looks up the central FIELD_EXPLANATIONS
 * map. Renders nothing when no text is available, so it is safe to drop next to any
 * value. The tooltip text is also exposed to screen readers (sr-only).
 */
import React from 'react';
import { Info } from 'lucide-react';
import { FIELD_EXPLANATIONS } from '../../utils/fieldExplanations';

interface MetricTooltipProps {
  description?: string;
  fieldKey?: string;
  className?: string;
}

const MetricTooltip: React.FC<MetricTooltipProps> = ({ description, fieldKey, className }) => {
  const text = description ?? (fieldKey ? FIELD_EXPLANATIONS[fieldKey] : undefined);
  if (!text) return null;
  return (
    <span className={`group relative inline-flex align-middle ml-1 cursor-help ${className ?? ''}`}>
      <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" aria-hidden="true" />
      <span className="sr-only">{text}</span>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg bg-iflow-900 px-3 py-2 text-xs font-normal normal-case tracking-normal text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50"
      >
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-iflow-900" />
      </span>
    </span>
  );
};

export default MetricTooltip;
